import '@/lib/heap/utils';

export const DEFAULT_HEAP_ONREADY_STALL_MS = 20_000;

/**
 * Public config `heapOnReadyStallMs` (string ms from API). When missing or invalid, uses {@link DEFAULT_HEAP_ONREADY_STALL_MS}.
 * Non-finite or negative values fall back to the default. Zero disables the stall callback (see {@link runWhenHeapReady}).
 */
export function getHeapOnReadyStallMsFromPublicConfig(
    configs: { [key: string]: string } | undefined,
): number {
    const raw = configs?.['heapOnReadyStallMs'];
    if (raw === undefined || raw.trim() === '') {
        return DEFAULT_HEAP_ONREADY_STALL_MS;
    }
    const n = Number.parseInt(raw.trim(), 10);
    if (!Number.isFinite(n) || n < 0) {
        return DEFAULT_HEAP_ONREADY_STALL_MS;
    }
    return n;
}

export type RunWhenHeapReadyOptions = {
    /**
     * When Heap uses `onReady`, fire `onStall` if the callback has not run after this many ms
     * (script blocked, network, or `onReady` never runs).
     */
    stallAfterMs?: number;
    onStall?: () => void;
    onError?: (error: unknown) => void;
};

/**
 * Run `fn` when Heap’s client is ready. The default loader sets stub methods that queue
 * until `heap_config.js` loads; `onReady` runs after the full client is up.
 * If `onReady` is missing, runs `fn` on the next macrotask so `initialize()` can finish first.
 *
 * @returns optional cleanup: clears the onReady stall timer (use in `useEffect` cleanup when deps change).
 */
export function runWhenHeapReady(
    fn: () => void,
    options?: RunWhenHeapReadyOptions,
): (() => void) | undefined {
    if (typeof globalThis === 'undefined' || typeof window === 'undefined' || !window.heap) {
        return undefined;
    }
    const { heap } = window;
    let completed = false;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    const clearStallTimer = (): void => {
        if (stallTimer !== null) {
            globalThis.clearTimeout(stallTimer);
            stallTimer = null;
        }
    };

    const run = (): void => {
        if (completed) {
            return;
        }
        try {
            if (!window.heap) {
                return;
            }
            fn();
        }
        catch (err) {
            options?.onError?.(err);
        }
        finally {
            completed = true;
            clearStallTimer();
        }
    };

    if (typeof heap.onReady === 'function') {
        const stallMs = options?.stallAfterMs ?? 0;
        if (stallMs > 0 && options?.onStall) {
            stallTimer = globalThis.setTimeout(() => {
                if (!completed) {
                    options.onStall!();
                }
            }, stallMs);
        }
        heap.onReady(() => {
            run();
        });
        return clearStallTimer;
    }

    globalThis.setTimeout(() => {
        run();
    }, 0);
    return clearStallTimer;
}
